import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";

import { useConfigurationQuery } from "../../../api/configuration.api";
import { useProgramState } from "../functions/program-state";
import { useRegistrationState } from "../functions/registration-state";
import { ReservationLinkType } from "../../../api/program.registration.models";

const ProgramRegistrationReservationNumber = () => {
	const { registration, updateRegistration, validationErrors } = useRegistrationState();
	const { programGroup } = useProgramState();
	const { data: configuration } = useConfigurationQuery();
	const [reservationOption, setReservationOption] = useState(registration.ReservationLink?.Type ?? ReservationLinkType.ReservationNumber);
	const [reservationNumber, setReservationNumber] = useState(registration.ReservationLink?.ReservationNumber ?? "");
	const [stayAddress, setStayAddress] = useState(registration.ReservationLink?.StayAddress ?? "");
	const [errorMessage, setErrorMessage] = useState<string|undefined>(undefined);

	useEffect(() => {
		const reservationLinkValidationErrors = validationErrors.filter(e => 
			e.Code.startsWith("ERR_RESERVATION_LINK_REQUIRED") || 
			e.Code.startsWith("ERR_RESERVATION_NUMBER_REQUIRED") || 
			e.Code.startsWith("ERR_STAY_LOCATION_REQUIRED") || 
			e.Code.startsWith("ERR_INVALID_RESERVATION_LINK_TYPE"));

		if (reservationLinkValidationErrors.length > 0) {
			setErrorMessage(reservationLinkValidationErrors[0].Message);
		} else {
			setErrorMessage(undefined);
		}
	}, [validationErrors]);

	useEffect(() => {
		if (registration && programGroup && programGroup.RequireReservationNumber) {
			registration.ReservationLink = {
				Type: reservationOption,
				ReservationNumber: reservationOption === ReservationLinkType.ReservationNumber ? reservationNumber : undefined,
				StayAddress: (reservationOption === ReservationLinkType.PrivateRental || reservationOption === ReservationLinkType.None) ? stayAddress : undefined
			}
			updateRegistration(registration);
			setErrorMessage(undefined);
		}
	}, [reservationOption, reservationNumber, stayAddress]);

	if (!programGroup || !programGroup.RequireReservationNumber) {
		return null;
	}

	const handleSetReservationOption = (value: ReservationLinkType) => {
		setReservationOption(value);
		setReservationNumber("");
	}

	return <>
		<div className="card shadow mb-3">
			<h5 className="card-header"><FontAwesomeIcon icon={faMapMarkerAlt} className="text-muted mr-2" />{configuration?.OrganizationShortform} Reservation Number or Stay Location</h5>

			<div className="card-body">
				<p className="lead font-weight-bold mb-4">Please provide your {configuration?.OrganizationShortform} reservation number if you have it. Otherwise, please tell us where you will be staying.</p>

				<div className="custom-control custom-radio">
					<input className="custom-control-input" type="radio" name="reservationNumberOption" id="reservationNumberOption1" value="1" checked={reservationOption === ReservationLinkType.ReservationNumber} onChange={() => handleSetReservationOption(ReservationLinkType.ReservationNumber)} />
					{import.meta.env.VITE_TITLE === "MBC Online Reservation" ? (
						<label className="custom-control-label mb-2" htmlFor="reservationNumberOption1">I have a {configuration?.OrganizationShortform} reservation number for an accommodation, campsite or MBC Acres cottage.</label>
					) : (
						<label className="custom-control-label mb-2" htmlFor="reservationNumberOption1">I have a {configuration?.OrganizationShortform} reservation number for an accommodation or campsite.</label>
					)}
				</div>
				
				<div className="custom-control custom-radio">
					<input className="custom-control-input" type="radio" name="reservationNumberOption" id="reservationNumberOption2" value="2" checked={reservationOption === ReservationLinkType.None} onChange={() => handleSetReservationOption(ReservationLinkType.None)} />
					<label className="custom-control-label mb-2" htmlFor="reservationNumberOption2">I don't have a {configuration?.OrganizationShortform} reservation number yet, but will be staying in {configuration?.OrganizationShortform} accommodations or the campground.</label>
				</div>
				
				<div className="custom-control custom-radio">
					<input className="custom-control-input" type="radio" name="reservationNumberOption" id="reservationNumberOption3" value="3" checked={reservationOption === ReservationLinkType.PrivateRental} onChange={() => handleSetReservationOption(ReservationLinkType.PrivateRental)} />
					<label className="custom-control-label mb-2" htmlFor="reservationNumberOption3">I am staying in a private rental, and don't have a {configuration?.OrganizationShortform} reservation number.</label>
				</div>

				{reservationOption === ReservationLinkType.ReservationNumber && <>
					<p className="mt-3">You can find your reservation number on your reservation confirmation, which should have been emailed to you from 
						the  {configuration?.OrganizationShortform} office when your reservation was received.</p>
					<p>
						You can also contact the {configuration?.OrganizationShortform} office at {configuration?.OrganizationPhone} if 
						you need assistance getting your reservation number.
					</p>
					
					<div className="row">
						<div className="col-12 col-sm-6">
							<div className="form-group mb-0">
								<div className="form-label-group mb-0">
									<input className="form-control" placeholder="Reservation number" value={reservationNumber} onChange={(e) => { setReservationNumber(e.target.value) }} name="reservationNumber" maxLength={20} />
									<label>{configuration?.OrganizationShortform} reservation number</label>
								</div>
							</div>
						</div>
					</div>
				</>}
				
				{reservationOption === ReservationLinkType.PrivateRental && <>
					<p className="mt-3">Please provide the campground site number or address where you will be staying.</p>
					
					<div className="row">
						<div className="col-12 col-sm-6">
							<div className="form-group mb-0">
								<div className="form-label-group mb-0">
									<input className="form-control" placeholder="Site # or address" value={stayAddress} onChange={(e) => { setStayAddress(e.target.value) }} name="stayAddress" maxLength={100} />
									<label>Site # or address</label>
								</div>
							</div>
						</div>
					</div>
				</>}
				
				{reservationOption === ReservationLinkType.None && <>
					<p className="mt-3">Please provide the room number or campground site number where you will be staying.</p>
					
					<div className="row">
						<div className="col-12 col-sm-6">
							<div className="form-group mb-0">
								<div className="form-label-group mb-0">
									<input className="form-control" placeholder="Room or Site #" value={stayAddress} onChange={(e) => { setStayAddress(e.target.value) }} name="stayAddress" maxLength={100} />
									<label>Room or Site #</label>
								</div>
							</div>
						</div>
					</div>
				</>}

				{errorMessage &&
				<div className="text-danger font-weight-bold mt-3">
					{errorMessage ?? "Please provide the required information."}
				</div>
				}
			</div>
		</div>	
	</>
}

export default ProgramRegistrationReservationNumber;